import * as React from 'react';
import styled from 'styled-components';
import { DateRangeTypeOption } from '../../../../data/providers/settings/dates/dateRangeProvider';
import {
  IWeightReportSettings,
  IWeightUnitOption,
} from '../../../../data/providers/settings/reports/weightReportSettingsProvider';
import { IRange } from '../../../../data/types';
import { Row } from '../../../layout/Row';
import { CardHeader, CardHeaderLeft } from '../../../shared/card/CardHeader';
import { ControlLabel } from '../../../shared/form/ControlLabel';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { Input } from '../../../shared/form/Input';
import { DateSelect } from '../../../shared/form/selects/DateRangeSelect';
import { WeightUnitSelect } from '../../../shared/form/selects/WeightUnitSelect';
import { Step } from '../../../shared/form/Step';
import { FormSection } from '../../utils/settings/FormSection';

interface IProps {
  onSettingsChange: (settings: IWeightReportSettings) => void;
  settings: IWeightReportSettings;
}

export const WeightForm = (props: IProps): React.ReactElement => {
  /**
   * @description handle changes to timeframe
   * @param timeframe
   */
  const timeframeChangeHandler = (
    dateType: DateRangeTypeOption,
    dateRange: IRange
  ): void => {
    props.onSettingsChange({
      ...props.settings,
      timeframe: {
        dateType,
        dateRange: dateRange ?? null,
      },
    });
  };

  /**
   * @description handle changes to baseline timeframe
   * @param dateType
   * @param dateRange
   */
  const baselineTimeframeChangeHandler = (
    dateType: DateRangeTypeOption,
    dateRange: IRange
  ): void => {
    props.onSettingsChange({
      ...props.settings,
      baselineTimeframe: {
        dateType,
        dateRange: dateRange ?? null,
      },
    });
  };

  /**
   * @description handles change to weight units
   * @param units
   */
  const weightUnitChangeHandler = (units: IWeightUnitOption): void => {
    props.onSettingsChange({
      ...props.settings,
      units,
    });
  };

  /**
   * @description handles change to lower % cutoff
   * @param event
   */
  const lowerCutoffChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const { value } = e.target;
    props.onSettingsChange({
      ...props.settings,
      changeCutoffs: {
        ...props.settings.changeCutoffs,
        lowerCutoff: +value,
      },
    });
  };

  /**
   * @description handles change to upper % cutoff
   * @param event
   */
  const upperCutoffChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const { value } = e.target;
    props.onSettingsChange({
      ...props.settings,
      changeCutoffs: {
        ...props.settings.changeCutoffs,
        upperCutoff: +value,
      },
    });
  };

  return (
    <div style={{ padding: '10px', paddingTop: '20px' }}>
      {/* Top Row */}
      <Row shouldCollapse style={{ alignItems: 'flex-start' }}>
        <LeftSide>
          <FormSection>
            <CardHeader>
              <CardHeaderLeft>
                <Step>1</Step>
                <h6>Select Timeframe</h6>
              </CardHeaderLeft>
            </CardHeader>
            <div style={{ padding: '10px' }}>
              <ControlSpacer>
                <DateSelect
                  type='inclusive'
                  onChange={timeframeChangeHandler}
                  dateRangeType={props.settings?.timeframe?.dateType}
                  dateRange={props.settings?.timeframe?.dateRange}
                />
              </ControlSpacer>
            </div>
          </FormSection>
        </LeftSide>
        <RightSide>
          <FormSection>
            <CardHeader>
              <CardHeaderLeft>
                <Step>2</Step>
                <h6>Select Baseline Timeframe</h6>
              </CardHeaderLeft>
            </CardHeader>
            <div style={{ padding: '10px' }}>
              <ControlSpacer>
                <DateSelect
                  type='exclusive'
                  onChange={baselineTimeframeChangeHandler}
                  dateRangeType={props.settings?.baselineTimeframe?.dateType}
                  dateRange={props.settings?.baselineTimeframe?.dateRange}
                />
              </ControlSpacer>
            </div>
          </FormSection>
        </RightSide>
      </Row>
      {/* Bottom Row */}
      <Row shouldCollapse style={{ alignItems: 'flex-start' }}>
        <LeftSide>
          <FormSection>
            <CardHeader>
              <CardHeaderLeft>
                <Step>3</Step>
                <h6>Select Units</h6>
              </CardHeaderLeft>
            </CardHeader>
            <div style={{ padding: '10px' }}>
              <ControlSpacer>
                <ControlLabel style={{ marginLeft: '10px' }}>
                  Units
                </ControlLabel>
                <WeightUnitSelect
                  style={{ marginLeft: '10px' }}
                  value={props?.settings?.units}
                  onChange={weightUnitChangeHandler}
                />
              </ControlSpacer>
            </div>
          </FormSection>
        </LeftSide>
        <RightSide>
          <FormSection>
            <CardHeader>
              <CardHeaderLeft>
                <Step>4</Step>
                <h6>Specify Percentage Cutoffs</h6>
              </CardHeaderLeft>
            </CardHeader>
            <div style={{ padding: '10px' }}>
              <p>
                This report will highlight in red any athlete weight gains OR
                losses that fall above the red zone value, and highlight in
                green any changes under the green zone value. Weight changes
                between these values will be highlighted in yellow - use the
                fields below to set desired green/red zone values.
              </p>
              <ControlSpacer>
                <Row>
                  <p>Green Zone:</p>
                  <Input
                    phColor='var(--color-control-text)'
                    style={{
                      background: '#4CAF5020',
                      margin: '0 15px',
                      width: '5%',
                    }}
                    onChange={lowerCutoffChangeHandler}
                    placeholder={`${props.settings?.changeCutoffs?.lowerCutoff.toString()}%`}
                  />
                  <p>Red Zone:</p>
                  <Input
                    style={{
                      background: '#F4433620',
                      margin: '0 15px',
                      width: '5%',
                    }}
                    phColor='var(--color-control-text)'
                    onChange={upperCutoffChangeHandler}
                    placeholder={`${props.settings?.changeCutoffs?.upperCutoff.toString()}%`}
                  />
                </Row>
              </ControlSpacer>
            </div>
          </FormSection>
        </RightSide>
      </Row>
    </div>
  );
};

const LeftSide = styled.div`
  flex: 1;
  width: 100%;
  @media (min-width: 991px) {
    margin-right: var(--grid-gap);
  }
`;

const RightSide = styled.div`
  width: 100%;
  flex: 1;
`;
